import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import { HttpStatusCode } from '@angular/common/http';
import { CustomMessageService, UtilService, LocalStorageService, ResponseHttpSuccess, ResponseHttpError } from 'library';
import { RequestService } from 'projects/store/src/app/services/api/v1/request.service';
import { LocalStorageKey, SalesWorkflow, SalesWorkflowSteps } from 'projects/store/src/app/enums';
import { environment } from 'projects/store/src/environments/environment';
import { AuthService } from '../../../services/api/v1/auth.service';
import {SalesWorkflowService} from "projects/store/src/app/services/api/v1/sales-workflow.service";

interface CreateRequestResponse {
  request_id: string;
  buyback: boolean;
  resume: boolean;
  approved: boolean;
  top_up_loan: boolean;
}

@Component({
  selector: 'app-create-request',
  templateUrl: './create-request.component.html'
})
export class CreateRequestComponent implements OnInit {
  @Output() nextStepEvent = new EventEmitter<any>();
  @Output() gotoStepEvent = new EventEmitter<any>();

  flowCode: string;
  categoryCode: string;
  requestId: string;
  clientId: string = '';
  kyc_last_session_id: string = '';
  telefono: string = '';
  isLoading = true;

  constructor(
    private route: ActivatedRoute,
    private requestService: RequestService,
    private authService: AuthService,
    private customMessageService: CustomMessageService,
    private utilService: UtilService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private salesWorkflowService: SalesWorkflowService
  ) {
    this.flowCode = this.route.snapshot.params['flow_code'];
    this.categoryCode = this.route.snapshot.params['category_code'];
    this.requestId = this.route.snapshot.params['request_id'];
  }

  ngOnInit(): void {
    this.utilService.disablePageLoading();
    this.extractClientIdFromToken();
    this.getTokenBeforeCreateRequest();
  }

  private extractClientIdFromToken(): void {
    const startToken = this.route.snapshot.queryParams['start_token'];

    if (startToken) {
      try {
        const decodedToken = JSON.parse(atob(startToken));
        this.kyc_last_session_id = decodedToken.kyc_last_session_id || '';
        this.telefono = decodedToken.telefono || '';
        this.localStorageService.set(LocalStorageKey.startToken, decodedToken);
        this.localStorageService.remove(LocalStorageKey.salesToken);
      } catch (e) {
        console.error('Error decoding start_token:', e);
      }
    } else {
      const storedToken = this.localStorageService.get(LocalStorageKey.startToken);
      if (storedToken) {
        this.kyc_last_session_id = storedToken.kyc_last_session_id || '';
        this.telefono = storedToken.telefono || '';
      }
    }
  }

  private buildTokenData(): { store_id: string; source: string; current_token: string } {
    const currentToken = this.localStorageService.get(LocalStorageKey.salesToken) || '';

    return {
      store_id: environment.global.appKrdyaStoreId,
      source: 'app-kredya',
      current_token: currentToken
    };
  }

  // Nuevo: Obtener token antes de crear la solicitud
  private getTokenBeforeCreateRequest(): void {
    const tokenData = this.buildTokenData();
    this.authService.getToken(tokenData).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok && resp.body?.data?.sales_token) {
          // Guardar el token en localStorage
          this.localStorageService.set(LocalStorageKey.salesToken, resp.body.data.sales_token);
          // Ahora sí llamar a createRequest
          this.createRequest();
        } else {
          this.customMessageService.responseNoticeHandler(resp);
          this.isLoading = false;
          this.utilService.disablePageLoading();
        }
      },
      error: (error) => {
        this.customMessageService.responseNoticeHandler(error);
        console.error('Error getting sales token:', error);
        this.isLoading = false;
        this.utilService.disablePageLoading();
      }
    });
  }

  private resolveNextStep(requestId: string): void {
    this.requestService.get(requestId).subscribe({
      next: (resp: any) => {
        if (resp.status === HttpStatusCode.Ok && resp.body?.data) {
          const currentCategoryStepId = resp.body.data.current_category_step_id;
          if (currentCategoryStepId) {
            this.navigateToStep(requestId, currentCategoryStepId);
          } else {
            this.nextStepEvent.emit({ requestId });
          }
        } else {
          this.nextStepEvent.emit({ requestId });
        }
      },
      error: () => {
        this.nextStepEvent.emit({ requestId });
      }
    });
  }

  private navigateToStep(requestId: string, categoryStepId: string): void {
    this.requestService.getInformationByCategoryStepId(categoryStepId).subscribe({
      next: (resp: any) => {
        if (resp.status === HttpStatusCode.Ok) {
          const categoryStep = resp.body.data;
          this.gotoStepEvent.emit({
            flowCode: categoryStep.flow.code,
            stepCode: categoryStep.code,
            categoryId: categoryStep.category?.id,
            categoryCode: categoryStep.category?.code,
            requestId
          });
        } else {
          this.nextStepEvent.emit({ requestId });
        }
      },
      error: () => {
        this.nextStepEvent.emit({ requestId });
      }
    });
  }

  createRequest(): void {
    if (!this.kyc_last_session_id && !this.telefono) {
      this.customMessageService.error('No se encontro el identificador del cliente');
      this.utilService.disablePageLoading();
      return;
    }

    this.isLoading = true;

    this.requestService.createAppKrdyaRequest({ kyc_last_session_id: this.kyc_last_session_id, telefono: this.telefono})
    .subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok || resp.status === HttpStatusCode.Created) {
          const data = resp.body?.data as CreateRequestResponse;
          const requestId = data?.request_id;
          const approved = data?.approved ?? false;

          if (approved) {
            this.resolveNextStep(requestId);
          } else {
            // No aprobado: ir a solicitud rechazada
            this.gotoStepEvent.emit({
              flowCode: this.flowCode,
              stepCode: SalesWorkflowSteps['not-approved-request']
            });
          }
        } else {
          this.customMessageService.responseNoticeHandler(resp);
          this.utilService.disablePageLoading();
        }
        this.isLoading = false;
      }, error: (error: ResponseHttpError) => {
        if(error.status == HttpStatusCode.NotAcceptable) {
          this.customMessageService.logErrorOnly(error)
          this.router.navigate([this.salesWorkflowService.getErrorNotApprovedRequestUrl()], { state: {
              messageError: error.error?.message,
              attemptedUrl: this.utilService.getCurrentUrl(),
            }});
        }else{
          this.customMessageService.responseNoticeHandler(error);
        }
        this.utilService.disablePageLoading()
      }
    });
  }
}
